import React, {Component, type Node} from 'react';
import {connect} from 'react-redux';
import {TransitionGroup} from 'react-transition-group';
import {defaultTo} from 'ramda';
import loadable from '@loadable/component';
import {configureScope} from '@sentry/react';

import type {ApplicationStoreType, ActionCreatorType} from 'constants/flow/flowTypes';
import type {RouterInfoType} from 'reducers/routerInfo/routerInfoType';
import {clearCategoryData} from 'actions/category/category';
import {getCategoryId} from 'selectors/category/category';
import {getRouterInfo} from 'actions/app/app';
import {setRouterInfo} from 'actions/routerInfo/routerInfo';
import {Animation, FADE_IN} from 'components/__helpers/animation/animation';
import {withSuspense, FullScreenLoader} from 'components/__helpers/withSuspense/withSuspense';
import {CategorySkeletonConnected} from '../../features/categoryRefinements/components/category/categorySkeleton/categorySkeleton';

type PropsType = {
    routerInfo: RouterInfoType,
    resolvePath: ActionCreatorType<string>,
    setPath: ActionCreatorType<RouterInfoType>,
    location: Location & {state: {page: string, params: {id: number}}},
    categoryId: number,
    clearCategoryData: () => void,
};

const CategoryRefinements = loadable(() =>
    import(
        /* webpackChunkName: "categoryRefinements" */ 'features/categoryRefinements/containers/category/category/category'
    ),
);

const Product = loadable(() =>
    import(/* webpackChunkName: "product" */ 'containers/product/product'),
);

const Cms = loadable(() => import(/* webpackChunkName: "cms" */ 'pages/cms/cms'));

const Error404 = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "error404" */ 'containers/error404/error404')),
);

/**
 * Resolves dynamic paths, that did't correspond any static router.
 *
 * In case if a route doesn't correspond any static router
 * and do not exist as a dynamic router, will be returned the 404 error component.
 */
export class DynamicPathResolver extends Component<PropsType> {
    /**
     * Called after the component is mounted
     */
    componentDidMount() {
        this.updateRouterInfo();
    }

    /**
     * Returns true if component should update with new props
     * @param {PropsType} nextProps - next props
     * @returns {Boolean} true if component should update with new props
     */
    shouldComponentUpdate(nextProps: PropsType): boolean {
        const {location, routerInfo} = this.props;
        return (
            nextProps.location.pathname !== location.pathname ||
            nextProps.routerInfo.page !== routerInfo.page ||
            nextProps.routerInfo.code !== routerInfo.code
        );
    }

    /**
     * Called after the component was updated
     * @param {PropsType} prevProps - next props
     */
    componentDidUpdate(prevProps: PropsType) {
        const prevLocation = `${prevProps.location.pathname}${prevProps.location.search}`;
        const nextLocation = `${this.props.location.pathname}${this.props.location.search}`;

        if (prevLocation !== nextLocation) {
            this.updateRouterInfo();
        }
    }

    /**
     * For the case of SPA move, this function clear prev data of the page to avoid blinking
     * @param {RouterInfoType} route - new route info
     */
    clearExistedDataIfNeeded(route: RouterInfoType) {
        if (route.page === 'category') {
            const {categoryId: oldId, clearCategoryData: clear} = this.props;
            if (oldId && oldId !== route.params.id) clear();
        }
    }

    /**
     * Update the router information
     */
    updateRouterInfo = () => {
        const {resolvePath, setPath, location, routerInfo} = this.props;

        if (typeof location.state === 'undefined' && !routerInfo.params.id) {
            resolvePath(`${location.pathname}${location.search}`);
        } else {
            const path = location.state || routerInfo;

            this.clearExistedDataIfNeeded(path);

            setPath({
                params: path.params,
                code: path.code,
                page: path.page,
            });
        }
    };

    /**
     * Switching between dynamic page containers.
     * @param {string} page - Page type
     * @param {string} id - Entity id
     * @returns {Node} - rendered component
     */
    renderTo = (page: string, id: string): Node => {
        switch (page) {
            case 'category':
                configureScope(scope => scope.setTransactionName(':category'));
                return (
                    <CategoryRefinements categoryId={id} fallback={<CategorySkeletonConnected />} />
                );
            case 'product':
                configureScope(scope => scope.setTransactionName(':product'));
                return <Product productId={id} fallback={<FullScreenLoader />} />;
            case 'page':
                configureScope(scope => scope.setTransactionName(':cms-page'));
                return <Cms pageId={id} fallback={<FullScreenLoader />} />;
            default:
                return <FullScreenLoader />;
        }
    };

    /**
     * Render component
     * @returns {Node} - rendered component
     */
    render(): Node {
        const {routerInfo, location} = this.props;
        const {page, params} = defaultTo(routerInfo, location.state);

        if (routerInfo.code === 0) return <Error404 />;

        return (
            <TransitionGroup>
                <Animation key={page} name={FADE_IN}>
                    {this.renderTo(page, params.id)}
                </Animation>
            </TransitionGroup>
        );
    }
}

export const mapStateToProps = (store: ApplicationStoreType) => ({
    routerInfo: store.routerInfo,
    categoryId: getCategoryId(store),
});

export const DynamicPathResolverConnected = connect(mapStateToProps, {
    resolvePath: getRouterInfo,
    setPath: setRouterInfo,
    clearCategoryData,
})(DynamicPathResolver);
